import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

import Loading from "components/Loading";

import councilService from "api/councilService";
import { ECouncilSessionState, ICouncilSession } from "dto/Council/ICouncil";
import { ENotificationType, notification } from "utils/notification";

import AgendaEditor from "./AgendaEditor";
import { decodeSession } from "./decodeText";
import styles from "./styles.module.scss";

// Biuro rady → „Archiwum sesji". Odwzorowanie
// Kwantum.Cases.UI.CouncilSessionAgenda.View.ArchiveControl.
//
// Widok jest dwustanowy:
//  A) kalendarz roku — 12 kafli miesięcy, dzień z sesją to kolorowe kółko
//     (pomarańcz = sesja archiwalna/w toku, zielony = sesja jeszcze nierozpoczęta),
//  B) szczegóły wybranej sesji z przełącznikiem Porządek obrad / Głosowania / Protokół.
//
// Jak w Magistracie kalendarz obejmuje WSZYSTKIE sesje (także bieżące), nie tylko
// zarchiwizowane — dlatego lista jest pobierana bez filtra `archived`.
//
// Różnice świadome: dodana legenda kolorów (w oryginale jej nie było), nagłówki dni
// tygodnia, poprawny nagłówek szczegółów (w Magistracie bindował się do nieistniejącej
// właściwości SelectedDay i zawsze był pusty) oraz przycisk powrotu do kalendarza.

const MONTHS = [
  "Sty",
  "Lut",
  "Mar",
  "Kwi",
  "Maj",
  "Cze",
  "Lip",
  "Sie",
  "Wrz",
  "Paź",
  "Lis",
  "Gru",
];

const WEEK_DAYS = ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "N"];

type Tab = "agenda" | "votings" | "protocol";

interface DayCell {
  day: number | null;
  isWeekend: boolean;
  sessions: ICouncilSession[];
}

const localDayKey = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

/** Siatka 6×7 dla miesiąca, tydzień zaczyna się od poniedziałku (kultura pl-PL). */
const buildMonth = (
  year: number,
  month: number,
  byDay: Map<string, ICouncilSession[]>,
): DayCell[] => {
  const first = new Date(year, month, 1);
  // getDay(): 0 = niedziela → przesuwamy tak, żeby poniedziałek był 0.
  const offset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: DayCell[] = [];

  for (let i = 0; i < 42; i += 1) {
    const day = i - offset + 1;

    if (day < 1 || day > daysInMonth) {
      cells.push({ day: null, isWeekend: false, sessions: [] });
      continue;
    }

    const date = new Date(year, month, day);

    cells.push({
      day,
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      sessions: byDay.get(`${year}-${month}-${day}`) ?? [],
    });
  }

  return cells;
};

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "";

const ArchiveView = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [selected, setSelected] = useState<ICouncilSession | null>(null);
  const [tab, setTab] = useState<Tab>("agenda");
  const [dayPopover, setDayPopover] = useState<string | null>(null);

  const yearsQuery = councilService.useGetArchiveYears();
  const { mutate: restore, isLoading: restoring } = councilService.useRestoreSession();
  // Bez filtra `archived` — kalendarz pokazuje wszystkie sesje danego roku.
  const sessionsQuery = councilService.useGetSessions({ year });

  const sessions = useMemo(
    () => (sessionsQuery.data?.data ?? []).map(decodeSession),
    [sessionsQuery.data],
  );

  const years = useMemo(() => {
    const fromApi = (yearsQuery.data?.data ?? []).map((y) => y.Year);
    return Array.from(new Set([...fromApi, new Date().getFullYear()])).sort(
      (a, b) => b - a,
    );
  }, [yearsQuery.data]);

  const byDay = useMemo(() => {
    const map = new Map<string, ICouncilSession[]>();

    sessions
      .filter((s) => s.Date)
      .forEach((s) => {
        const key = localDayKey(s.Date as string);
        map.set(key, [...(map.get(key) ?? []), s]);
      });

    return map;
  }, [sessions]);

  useEffect(() => {
    setSelected(null);
    setTab("agenda");
  }, [year]);

  // „Przenieś do archiwum" jest w tym porcie dostępne także dla sesji w przygotowaniu,
  // więc musi istnieć droga powrotna — inaczej sesja wpada do archiwum bez wyjścia.
  const restoreSelected = () => {
    if (!selected) return;

    restore(selected.Id, {
      onSuccess: () => {
        notification({
          type: ENotificationType.SUCCESS,
          title: "Przywrócono sesję",
          text: `Sesja ${selected.Number ?? ""} wróciła na listę w widoku „Sesja".`,
        });
        setSelected(null);
        sessionsQuery.refetch();
      },
      onError: () =>
        notification({
          type: ENotificationType.ERROR,
          title: "Błąd",
          text: "Nie udało się przywrócić sesji z archiwum.",
        }),
    });
  };

  if (sessionsQuery.isLoading) {
    return <Loading />;
  }

  if (selected) {
    return (
      <div className={styles.panel}>
        <div className={styles.archiveDetailHeader}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => setSelected(null)}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Kalendarz
          </button>

          <span className={styles.detailDate}>{formatDate(selected.Date)}</span>
          <span className={styles.detailTitle}>{selected.Number}</span>

          {selected.IsArchived && (
            <button
              type="button"
              className={styles.backButton}
              disabled={restoring}
              onClick={restoreSelected}
            >
              Przywróć z archiwum
            </button>
          )}

          <div className={styles.spacer} />

          <div className={styles.tabs}>
            <button
              type="button"
              className={`${styles.tab} ${tab === "agenda" ? styles.tabActive : ""}`}
              onClick={() => setTab("agenda")}
            >
              Porządek obrad
            </button>
            <button
              type="button"
              className={`${styles.tab} ${tab === "votings" ? styles.tabActive : ""}`}
              onClick={() => setTab("votings")}
            >
              Głosowania
            </button>
            <button
              type="button"
              className={`${styles.tab} ${tab === "protocol" ? styles.tabActive : ""}`}
              onClick={() => setTab("protocol")}
            >
              Protokół
            </button>
          </div>
        </div>

        {tab === "agenda" && (
          <AgendaEditor key={selected.Id} session={selected} forceReadOnly />
        )}

        {tab === "votings" && (
          <div className={styles.tabPlaceholder}>
            Głosowania będą dostępne po uruchomieniu prowadzenia obrad.
          </div>
        )}

        {tab === "protocol" && (
          <div className={styles.tabPlaceholder}>
            {selected.State === ECouncilSessionState.Finished
              ? "Protokół będzie dostępny po uruchomieniu modułu protokołu."
              : "Protokół będzie dostępny po zakończeniu sesji."}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.archiveHeader}>
        <span className={styles.archiveTitle}>Archiwum</span>

        <select
          className={styles.yearSelect}
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>

        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendDotSession}`} />
            sesja archiwalna lub w toku
          </span>
          <span className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendDotAwaiting}`} />
            sesja oczekująca
          </span>
        </div>
      </div>

      <div className={styles.calendar}>
        {MONTHS.map((name, month) => (
          <div key={name} className={styles.month}>
            <div className={styles.monthName}>{name}</div>

            <div className={styles.weekDays}>
              {WEEK_DAYS.map((d) => (
                <span key={d} className={styles.weekDay}>
                  {d}
                </span>
              ))}
            </div>

            <div className={styles.days}>
              {buildMonth(year, month, byDay).map((cell, index) => {
                if (cell.day === null) {
                  return (
                    <span
                      key={`${year}-${month}-empty-${index}`}
                      className={styles.dayEmpty}
                    />
                  );
                }

                const key = `${year}-${month}-${cell.day}`;
                const hasSession = cell.sessions.length > 0;
                // „Oczekująca" wygrywa nad „archiwalną" — jak DataTrigger IsAwaiting
                // nadpisujący pomarańcz w Magistracie (minimum stanów).
                const isAwaiting =
                  hasSession &&
                  Math.min(...cell.sessions.map((s) => s.State)) ===
                    ECouncilSessionState.NotStarted;

                return (
                  <span key={key} style={{ position: "relative" }}>
                    <button
                      type="button"
                      className={[
                        styles.day,
                        cell.isWeekend ? styles.dayWeekend : "",
                        hasSession
                          ? isAwaiting
                            ? styles.dayAwaiting
                            : styles.daySession
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      title={cell.sessions.map((s) => s.Number).join(", ")}
                      onClick={() => {
                        if (!hasSession) return;

                        if (cell.sessions.length === 1) {
                          setSelected(cell.sessions[0]);
                          setTab("agenda");
                          return;
                        }

                        setDayPopover(dayPopover === key ? null : key);
                      }}
                    >
                      {cell.day}
                    </button>

                    {dayPopover === key && (
                      <div className={styles.daySessionsPopover}>
                        {cell.sessions.map((s) => (
                          <button
                            key={s.Id}
                            type="button"
                            className={styles.menuItem}
                            onClick={() => {
                              setDayPopover(null);
                              setSelected(s);
                              setTab("agenda");
                            }}
                          >
                            {s.Number}
                          </button>
                        ))}
                      </div>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ArchiveView;
